/**
 * AddEventGuardianDialog
 *
 * Modal launched from the EventGuardiansPanel "Add Guardian" button. Lets
 * an authorized operator attach an existing RosterPerson as a guardian on
 * an active reunification event via `useCreateEventGuardian` (POST
 * `/events/{event_public_id}/guardians/`).
 *
 * The picker (GuardianPicker) excludes roster persons already attached as
 * guardians so the operator can't double-add. Only `roster_person` is
 * required by the backend; verification fields (`verification_method`,
 * `id_type`) and the `authorized_students` M2M are typically populated
 * later via the verify / pre_verify / match_students actions. v1 keeps
 * the dialog focused on the add step.
 *
 * Permission gating happens in the parent panel via FeatureCode.REUNIFICATION
 * — the backend's `CanOperateGuardianVerification` is the load-bearing
 * check and will 403 a manual API call even with the feature flag flipped.
 *
 * ## Compliance gate
 *
 * Restricted-PII tier (guardian contact info). CCPA/CPRA in scope. The
 * backend writes a `guardian_added` EventAssignmentLog row inside
 * `perform_create` so the privacy audit-log has a "who added which
 * guardian, when" trail (ADR-099 §Layer 5).
 */

import { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Dialog,
  Field,
  HStack,
  Portal,
  Switch,
  Text,
  VStack,
} from '@chakra-ui/react';
import { Button } from '@themes/aegix';
import { useCreateEventGuardian } from '../../hooks/queries/useReunification';
import { toaster } from '../ui/toaster';
import type { GuardianReunification } from '../../types/ReunificationTypes';
import { GuardianPicker, type GuardianPickerSelection } from './GuardianPicker';

export interface AddEventGuardianDialogProps {
  /** Whether the dialog is open. */
  isOpen: boolean;
  /** Called when the dialog should close (cancel, escape, backdrop click). */
  onClose: () => void;
  /** Reunification event public_id receiving the new guardian. */
  eventId: string;
  /**
   * RosterPerson ids that are already attached as guardians on the event;
   * excluded from the picker so the operator can't create duplicates.
   */
  existingGuardianRosterPersonIds?: number[];
  /** Called after a successful create with the new guardian row. */
  onCreated?: (created: GuardianReunification) => void;
}

export function AddEventGuardianDialog({
  isOpen,
  onClose,
  eventId,
  existingGuardianRosterPersonIds,
  onCreated,
}: AddEventGuardianDialogProps) {
  const [selected, setSelected] = useState<GuardianPickerSelection | null>(
    null,
  );
  const [isPreVerified, setIsPreVerified] = useState(false);

  const createMutation = useCreateEventGuardian(eventId);
  const submitting = createMutation.isPending;

  // Reset state every time the dialog opens — avoid leaking the prior pick
  // into a subsequent add.
  useEffect(() => {
    if (isOpen) {
      setSelected(null);
      setIsPreVerified(false);
    }
  }, [isOpen]);

  const canSubmit = useMemo(
    () => !!selected && !submitting,
    [selected, submitting],
  );

  async function handleSubmit() {
    if (!selected) return;
    try {
      const created = await createMutation.mutateAsync({
        roster_person: selected.rosterPersonId,
        is_pre_verified: isPreVerified || undefined,
      });
      toaster.create({
        title: 'Guardian added',
        description: `${selected.name} attached to the event.`,
        type: 'success',
      });
      onCreated?.(created);
      onClose();
    } catch (err) {
      toaster.create({
        title: 'Could not add guardian',
        description: err instanceof Error ? err.message : 'Unknown error',
        type: 'error',
      });
    }
  }

  return (
    <Dialog.Root
      open={isOpen}
      onOpenChange={(details) => {
        if (!details.open && !submitting) onClose();
      }}
      placement="center"
      size="md"
      closeOnInteractOutside={!submitting}
      closeOnEscape={!submitting}
    >
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content
            bgColor="surface.primary"
            borderRadius="lg"
            maxWidth="560px"
            data-e2e="add-event-guardian-dialog"
          >
            <Dialog.Header p="20px 20px 8px 20px">
              <Dialog.Title
                fontSize="16px"
                fontWeight={600}
                color="text.primary"
              >
                Add guardian to event
              </Dialog.Title>
            </Dialog.Header>

            <Dialog.Body p="0 20px 16px 20px">
              <VStack align="stretch" gap={4}>
                <Box>
                  <Text
                    fontSize="sm"
                    fontWeight="semibold"
                    color="text.secondary"
                    mb={2}
                  >
                    Guardian
                  </Text>
                  <GuardianPicker
                    selected={selected}
                    onSelect={setSelected}
                    excludeRosterPersonIds={existingGuardianRosterPersonIds}
                    dataE2ePrefix="add-event-guardian-picker"
                  />
                </Box>

                <Field.Root>
                  <HStack justify="space-between" align="center" width="100%">
                    <VStack align="flex-start" gap={0}>
                      <Field.Label fontSize="sm" color="text.primary">
                        Mark as pre-verified
                      </Field.Label>
                      <Field.HelperText fontSize="xs" color="text.secondary">
                        Use only when the guardian has already been
                        verified via the ONE app (recorded in the audit
                        log).
                      </Field.HelperText>
                    </VStack>
                    <Switch.Root
                      checked={isPreVerified}
                      onCheckedChange={(details) =>
                        setIsPreVerified(details.checked)
                      }
                      disabled={submitting}
                    >
                      <Switch.HiddenInput
                        data-e2e="add-event-guardian-pre-verified"
                      />
                      <Switch.Control>
                        <Switch.Thumb />
                      </Switch.Control>
                    </Switch.Root>
                  </HStack>
                </Field.Root>

                <Text fontSize="xs" color="text.tertiary">
                  Verification details (ID type, method) and authorized
                  students can be set from the guardian detail drawer once
                  the row is added. Walk-in (unrostered) guardians are
                  tracked as a follow-up under ASM-278.
                </Text>
              </VStack>
            </Dialog.Body>

            <Dialog.Footer p="0 20px 20px 20px">
              <HStack justify="flex-end" gap={2} width="100%">
                <Button
                  visual="secondary"
                  type="button"
                  onClick={onClose}
                  disabled={submitting}
                  data-e2e="add-event-guardian-cancel"
                >
                  Cancel
                </Button>
                <Button
                  visual="primary"
                  type="button"
                  onClick={handleSubmit}
                  disabled={!canSubmit}
                  data-e2e="add-event-guardian-submit"
                >
                  {submitting ? 'Adding…' : 'Add guardian'}
                </Button>
              </HStack>
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
}

export default AddEventGuardianDialog;
